import { t } from 'i18next';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { QuestionOption, SendingResponse, Survey } from '../../types/surveyData';
import { QuestionParam } from '../../types/questionData';
import moment from 'moment';

export enum SurveyType {
  Participate = 'Participate',
  Build = 'Build',
}

export enum SurveyTypeLabel {
  Participate = '설문 참여함',
  Build = '설문 제작함',
}

export enum BuildType {
  All = 'ALL',
  InProduction = 'IN_PRODUCTION',
  Waiting = 'WAITING',
  Distributing = 'DISTRIBUTING',
  End = 'END',
}

export enum BuildTypeLabel {
  ALL = '전체',
  IN_PRODUCTION = '제작중',
  WAITING = '시작대기',
  DISTRIBUTING = '배포중',
  END = '종료',
}

export enum ParticipateType {
  All = 'All',
  None = 'None',
  Done = 'Done',
}

export enum ParticipateTypeLabel {
  All = '전체',
  None = '미참여',
  Done = '참여완료',
}

export enum TabType {
  Builder = 'Builder',
  Share = 'Share',
  Result = 'Result',
}

export enum QueType {
  Emotion = 'EMOTION', // 감정바
  Essay = 'ESSAY', // 주관식
  Image = 'IMAGE', // 객관식 이미지
  Linear = 'LINEAR', // 선형배율
  Multiple = 'MULTIPLE', // 객관식
  Rating = 'RATING', //별점
  Subtheme = 'SUBTHEME', // 소주제
}

export enum GuideType {
  Submit = 'Submit', // 제출 완료
  End = 'End', // 설문 종료
  Done = 'Done', // 응답 완료 + 수정 불가
  Auth = 'Auth', // 응답 권한 없음
  NotViewer = 'NotViewer', // 편집화면 view 권한 없음
  BeforeStart = 'BeforeStart',
  Deleted = 'Deleted',
}

export interface Guide {
  guideType: GuideType | null;
  survey: Survey | null;
}

export interface HomeLayer {
  participate: boolean;
  build: boolean;
}

export interface SurveyState {
  list: Survey[];
  detail: Survey;
  type: SurveyType;
  subType: ParticipateType | BuildType;
  tab: TabType;
  guide: Guide;
  target: string | null;
  required: SendingResponse[];
  mobileMenu: boolean;
  homeLayer: HomeLayer;
  saved: boolean;
  autoSaveDt: string;
  openPreview: boolean;
  clickedQue: string | null;
}

const initialState: SurveyState = {
  list: [] as Survey[],
  detail: {} as Survey,
  type: SurveyType.Participate,
  subType: ParticipateType.All,
  tab: TabType.Builder,
  guide: {} as Guide,
  target: null,
  required: [],
  mobileMenu: false,
  homeLayer: { participate: true, build: true },
  saved: true,
  autoSaveDt: '',
  openPreview: false,
  clickedQue: null,
};

export const surveySlice = createSlice({
  name: 'survey',
  initialState,
  reducers: {
    setSurveyType(state, action: PayloadAction<any>) {
      state.type = action.payload;
      state.subType = action.payload === SurveyType.Participate ? ParticipateType.All : BuildType.All;
      state.mobileMenu = false;
    },
    setSurveyList(state, action: PayloadAction<Survey[] | undefined>) {
      state.list = action.payload !== undefined ? action.payload : [];
    },
    setSubType(state, action: PayloadAction<ParticipateType | BuildType>) {
      state.subType = action.payload;
    },
    setTab(state, action: PayloadAction<TabType>) {
      state.tab = action.payload;
    },
    setGuide(state, action: PayloadAction<Guide>) {
      state.guide = action.payload;
    },
    setSurveyDetail(state, action: PayloadAction<{ survey: Survey; saved: boolean }>) {
      state.detail = action.payload.survey;
      state.saved = action.payload.saved;
      state.target = action.payload.survey?.surveyId ? state.target : null;
      if (action.payload.survey && action.payload.saved) {
        state.autoSaveDt = action.payload.survey.modifiedAt;
      }
    },
    setSurveyQue(state, action: PayloadAction<{ que: QuestionParam; saved: boolean }>) {
      state.target = action.payload.que.questionId;
      const newQue: QuestionParam = { ...action.payload.que };
      const exceptQue: QuestionParam[] = state.detail.questions?.filter(
        (item) => item.questionId !== action.payload.que.questionId,
      );
      let newQuestions: QuestionParam[] = exceptQue ? exceptQue : [];
      newQuestions.push(newQue);
      state.detail.questions = newQuestions;
      state.saved = action.payload.saved;
    },
    setSurveyMultipleQue(state, action: PayloadAction<{ queId: string; option: QuestionOption; saved: boolean }>) {
      state.target = action.payload.queId;
      if (state.detail && state.detail.questions) {
        const stateQue = state.detail.questions.filter((item) => item.questionId === action.payload.queId)[0];
        const exceptOpt: QuestionOption[] = stateQue?.questionOptions.filter(
          (item) => item.optionId !== action.payload.option.optionId,
        );
        const newOpts: QuestionOption[] = exceptOpt ? exceptOpt : [];
        newOpts.push({ ...action.payload.option });
        const newQue: QuestionParam = { ...stateQue, questionOptions: newOpts };

        const exceptQue: QuestionParam[] = state.detail.questions.filter(
          (item) => item.questionId !== action.payload.queId,
        );
        let newQuestions: QuestionParam[] = exceptQue ? exceptQue : [];
        newQuestions.push(newQue);
        state.detail.questions = newQuestions;
        state.saved = action.payload.saved;
      }
    },
    setRequiredQues(state, action: PayloadAction<SendingResponse[]>) {
      state.required = action.payload;
    },
    setTarget(state, action: PayloadAction<string>) {
      state.target = action.payload;
    },
    setMobileMenuOpen(state, action: PayloadAction<boolean>) {
      state.mobileMenu = action.payload;
    },
    setHomeLayer(state, action: PayloadAction<HomeLayer>) {
      state.homeLayer = action.payload;
    },
    setSaved(state, action: PayloadAction<boolean>) {
      state.saved = action.payload;
      if (action.payload) {
        state.autoSaveDt = moment().format('YYYY-MM-DD HH:mm:ss');
      }
    },
    setOpenPreview(state, action: PayloadAction<boolean>) {
      state.openPreview = action.payload;
    },
    setClickedQue(state, action: PayloadAction<string>) {
      state.clickedQue = action.payload;
    },
  },
});

export const {
  setSurveyType,
  setSurveyList,
  setSubType,
  setTab,
  setGuide,
  setSurveyDetail,
  setSurveyQue,
  setSurveyMultipleQue,
  setRequiredQues,
  setTarget,
  setMobileMenuOpen,
  setHomeLayer,
  setSaved,
  setOpenPreview,
  setClickedQue,
} = surveySlice.actions;
export default surveySlice;

// export const surveySelector = (state: any) => state.survey;
